/**
 * CRM Import Hub — Neuralia369
 * Importa CSV/Excel, segmenta por nicho, lanza campañas masivas
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Users,
  MessageCircle, Mail, Send, ChevronRight, X, Trash2, RefreshCw,
  Download, Zap, Filter, Eye, PlusCircle, BarChart3, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: authFetch devuelve Response cruda — parseamos JSON siempre
async function jFetch(authFetch, url, opts = {}) {
  const isForm = opts.body instanceof FormData;
  const res = await authFetch(url, {
    ...opts,
    headers: isForm ? opts.headers : { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.detail || json?.message || 'Error del servidor');
  return json;
}

const NICHOS = [
  'Estética & Belleza', 'Clínicas & Salud', 'MLM & Network Marketing',
  'Distribuidores', 'Coaches & Consultores', 'Restaurantes & Hostelería',
  'Inmobiliaria', 'Educación & Academia', 'Tecnología', 'Retail & E-commerce',
  'Seguros & Finanzas', 'Agencias de Marketing', 'Otro',
];

const CHANNEL_CONFIG = {
  whatsapp: { color: '#25D366', bg: 'bg-[#25D366]/10', border: 'border-[#25D366]/30', label: 'WhatsApp', icon: MessageCircle },
  email: { color: '#6366F1', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', label: 'Email', icon: Mail },
};

// ── Subcomponente: Tarjeta de segmento ────────────────────────────────────────
function SegmentCard({ seg, onCreateCampaign }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 flex items-center justify-between gap-4 hover:border-slate-700 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-[#00d4ff]/10 border border-[#00d4ff]/20 flex items-center justify-center flex-shrink-0">
          <Users className="w-4 h-4 text-[#00d4ff]" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-white text-sm truncate">{seg.tag}</div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{seg.total} contactos</span>
            <span className="text-[#25D366]">{seg.wa_ready} WA listos</span>
            <span className="text-indigo-400">{seg.email_ready} email</span>
          </div>
        </div>
      </div>
      <button
        onClick={() => onCreateCampaign(seg)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00d4ff]/10 border border-[#00d4ff]/20 text-[#00d4ff] text-xs font-medium rounded-xl hover:bg-[#00d4ff]/20 transition-colors whitespace-nowrap"
      >
        <Zap className="w-3.5 h-3.5" /> Lanzar campaña
      </button>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function CRMImportHub({ authFetch }) {
  const fileRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [nicho, setNicho] = useState('');
  const [nichoCustom, setNichoCustom] = useState('');
  const [consent, setConsent] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [toast, setToast] = useState('');

  // Modal campaña
  const [campaignModal, setCampaignModal] = useState(null); // { seg }
  const [campForm, setCampForm] = useState({ name: '', channel: 'whatsapp', message: '', subject: '' });
  const [sending, setSending] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genMessages, setGenMessages] = useState(null); // {visual, auditivo, kinestesico}

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const loadSegments = useCallback(async () => {
    setLoadingSegments(true);
    try {
      const data = await jFetch(authFetch, `${API}/crm/segments`);
      setSegments(data?.segments || []);
    } catch (e) {
      showToast('Error cargando segmentos');
    } finally {
      setLoadingSegments(false);
    }
  }, [authFetch]);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setImportResult(null);
    setPreview(null);
    // Preview
    const fd = new FormData();
    fd.append('file', f);
    try {
      const data = await jFetch(authFetch, `${API}/crm/contacts/preview`, { method: 'POST', body: fd });
      setPreview(data);
    } catch (e) {
      showToast('No se pudo leer el archivo. ¿Es CSV o Excel?');
      setFile(null);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, []);

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    const effectiveNicho = nicho === 'Otro' ? nichoCustom : nicho;
    const params = new URLSearchParams({ nicho: effectiveNicho, consent_default: consent });
    try {
      const data = await jFetch(authFetch, `${API}/crm/contacts/import?${params}`, {
        method: 'POST', body: fd,
      });
      setImportResult(data);
      showToast(`✅ Importados ${data.inserted} nuevos, ${data.updated} actualizados`);
      setFile(null);
      setPreview(null);
      loadSegments();
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const generateAIMessage = async () => {
    if (!campaignModal) return;
    setGenLoading(true);
    try {
      const res = await jFetch(authFetch, `${API}/crm/campaigns/generate-message`, {
        method: 'POST',
        body: JSON.stringify({
          nicho: campaignModal.seg.tag,
          canal: campForm.channel,
          objetivo: 'ofrecer automatización con IA para mejorar ventas y atención al cliente',
        }),
      });
      setGenMessages(res.messages);
    } catch (e) {
      showToast('Error generando mensaje');
    } finally {
      setGenLoading(false);
    }
  };

  const launchCampaign = async () => {    if (!campaignModal) return;
    setSending(true);
    try {
      const seg = campaignModal.seg;
      const camp = await jFetch(authFetch, `${API}/crm/campaigns`, {
        method: 'POST',
        body: JSON.stringify({
          name: campForm.name || `Campaña ${seg.tag}`,
          channel: campForm.channel,
          message: campForm.message,
          subject: campForm.subject,
          tags: [seg.tag],
          only_consent: campForm.channel === 'whatsapp',
        }),
      });
      const result = await jFetch(authFetch, `${API}/crm/campaigns/${camp.campaign_id}/send`, { method: 'POST' });
      showToast(`✅ Campaña enviada — ${result.sent} mensajes enviados`);
      setCampaignModal(null);
      loadSegments();
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const effectiveNicho = nicho === 'Otro' ? nichoCustom : nicho;
  const canImport = file && effectiveNicho;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">CRM — Bases de Datos</h2>
          <p className="text-slate-400 text-sm mt-1">Importa contactos, segmenta por nicho y lanza campañas masivas</p>
        </div>
        <button
          onClick={loadSegments}
          disabled={loadingSegments}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loadingSegments ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Upload zone */}
      <div
        data-testid="crm-upload-zone"
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          dragging ? 'border-[#00d4ff] bg-[#00d4ff]/5' : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/30'
        }`}
      >
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
          onChange={e => handleFile(e.target.files[0])} />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-[#00d4ff]" />
            <div className="text-left">
              <p className="text-white font-semibold">{file.name}</p>
              {preview && <p className="text-slate-400 text-sm">{preview.total_rows} filas detectadas</p>}
            </div>
            <button onClick={e => { e.stopPropagation(); setFile(null); setPreview(null); }}
              className="ml-4 p-1.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg hover:bg-red-500/20">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-white font-semibold">Arrastra tu CSV o Excel aquí</p>
            <p className="text-slate-500 text-sm mt-1">o haz clic para seleccionar · .csv, .xlsx</p>
            <p className="text-slate-600 text-xs mt-2">Columnas detectadas automáticamente: nombre, teléfono, email, etiquetas</p>
          </>
        )}
      </div>

      {/* Preview table */}
      {preview && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-white">
                Vista previa ({Math.min(8, preview.total_rows)} de {preview.total_rows} filas)
              </span>
            </div>
            <div className="flex gap-2">
              {Object.entries(preview.mapping).map(([field, col]) => (
                <span key={field} className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded-lg">
                  {field} ← {col}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  {['Nombre', 'Teléfono', 'Email', 'Tags'].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-slate-500 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((row, i) => (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                    <td className="px-4 py-2 text-white">{row.name || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-2 text-slate-300">{row.phone || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-2 text-slate-300">{row.email || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-2 text-slate-500">{row.tags || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Config: nicho + consent */}
      {preview && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Nicho / Segmento *</label>
            <select
              data-testid="crm-nicho-select"
              value={nicho}
              onChange={e => setNicho(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
            >
              <option value="">Seleccionar nicho...</option>
              {NICHOS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {nicho === 'Otro' && (
              <input
                type="text"
                placeholder="Escribe el nicho personalizado"
                value={nichoCustom}
                onChange={e => setNichoCustom(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#00d4ff]/50"
              />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Consentimiento</label>
            <div
              onClick={() => setConsent(c => !c)}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                consent ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800 border-slate-700'
              }`}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                consent ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'
              }`}>
                {consent && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </div>
              <div>
                <div className={`text-sm font-medium ${consent ? 'text-emerald-400' : 'text-slate-300'}`}>
                  Consentimiento otorgado
                </div>
                <div className="text-xs text-slate-500">Necesario para envíos WhatsApp</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import button */}
      {preview && (
        <button
          data-testid="crm-import-btn"
          onClick={doImport}
          disabled={!canImport || importing}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-6 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {importing ? (
            <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Importando...</>
          ) : (
            <><Upload className="w-4 h-4" /> Importar {preview.total_rows} contactos → {effectiveNicho || 'nicho requerido'}</>
          )}
        </button>
      )}

      {/* Import result */}
      {importResult && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 flex-shrink-0" />
          <div>
            <p className="font-semibold text-emerald-400">¡Base de datos importada!</p>
            <p className="text-slate-400 text-sm">
              {importResult.inserted} nuevos · {importResult.updated} actualizados · {importResult.skipped} omitidos
            </p>
          </div>
        </div>
      )}

      {/* Segments */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <h3 className="text-lg font-bold text-white">Segmentos / Nichos</h3>
          <span className="text-slate-500 text-sm">({segments.length} segmentos, {segments.reduce((a, s) => a + s.total, 0)} contactos totales)</span>
        </div>
        {segments.length === 0 ? (
          <div className="text-center py-10 text-slate-600">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Importa tu primera base de datos para ver los segmentos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {segments.map(seg => (
              <SegmentCard key={seg.tag} seg={seg} onCreateCampaign={s => {
                setCampaignModal({ seg: s });
                setCampForm({ name: `Campaña ${s.tag}`, channel: 'whatsapp', message: '', subject: '' });
                setGenMessages(null);
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Campaign modal */}
      {campaignModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f1117] border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-800">
              <div>
                <h3 className="font-bold text-white text-lg">Lanzar Campaña</h3>
                <p className="text-slate-400 text-sm">
                  Segmento: {campaignModal.seg.tag} · {campaignModal.seg.wa_ready} WA / {campaignModal.seg.email_ready} email listos
                </p>
              </div>
              <button onClick={() => setCampaignModal(null)} className="p-2 text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Canal */}
              <div className="grid grid-cols-2 gap-3">
                {['whatsapp', 'email'].map(ch => {
                  const cfg = CHANNEL_CONFIG[ch];
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={ch}
                      data-testid={`campaign-channel-${ch}`}
                      onClick={() => setCampForm(f => ({ ...f, channel: ch }))}
                      className={`p-3 rounded-xl border flex items-center gap-2 transition-all ${
                        campForm.channel === ch ? `${cfg.bg} ${cfg.border}` : 'bg-slate-800 border-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" style={{ color: campForm.channel === ch ? cfg.color : '#64748b' }} />
                      <span className={`text-sm font-medium ${campForm.channel === ch ? 'text-white' : 'text-slate-400'}`}>
                        {cfg.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Nombre */}
              <input
                type="text"
                placeholder="Nombre de la campaña"
                value={campForm.name}
                onChange={e => setCampForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
              />
              {/* Asunto email */}
              {campForm.channel === 'email' && (
                <input
                  type="text"
                  placeholder="Asunto del email"
                  value={campForm.subject}
                  onChange={e => setCampForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50"
                />
              )}
              {/* Mensaje + botón IA */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400">Mensaje</label>
                  <button
                    data-testid="generate-wa-msg-btn"
                    onClick={generateAIMessage}
                    disabled={genLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-500/15 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/25 disabled:opacity-50 transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {genLoading ? 'Generando...' : 'Generar con IA (NLP + VAK)'}
                  </button>
                </div>
                {/* Opciones VAK generadas */}
                {genMessages && (
                  <div className="space-y-1.5">
                    {Object.entries(genMessages).map(([tipo, msg]) => (
                      <button
                        key={tipo}
                        onClick={() => setCampForm(f => ({ ...f, message: msg.texto }))}
                        className={`w-full text-left p-2.5 rounded-xl border text-xs transition-colors hover:border-purple-500/40 ${
                          campForm.message === msg.texto
                            ? 'bg-purple-500/15 border-purple-500/40 text-purple-200'
                            : 'bg-slate-800 border-slate-700 text-slate-300'
                        }`}
                      >
                        <div className="font-semibold text-purple-400 mb-0.5 capitalize">
                          {tipo === 'visual' ? '👁 Visual' : tipo === 'auditivo' ? '👂 Auditivo' : '✋ Kinestésico'}
                          {' · '}<span className="text-slate-500 font-normal">{msg.hook}</span>
                        </div>
                        <div className="line-clamp-2 text-slate-400">{msg.texto}</div>
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  data-testid="campaign-message-input"
                  placeholder={campForm.channel === 'whatsapp'
                    ? "Hola {nombre}, soy Aynara de Neuralia369... (o genera con IA arriba)"
                    : "Escribe el contenido del email..."}
                  value={campForm.message}
                  onChange={e => setCampForm(f => ({ ...f, message: e.target.value }))}
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00d4ff]/50 resize-none"
                />
                <p className="text-slate-600 text-xs">
                  Usa {'{nombre}'} para personalizar. WhatsApp solo envía a contactos con consentimiento.
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-slate-800">
              <button onClick={() => setCampaignModal(null)}
                className="flex-1 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors text-sm">
                Cancelar
              </button>
              <button
                data-testid="campaign-send-btn"
                onClick={launchCampaign}
                disabled={!campForm.message || sending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#00d4ff] text-[#030712] font-bold rounded-xl hover:bg-[#00bfea] disabled:opacity-40 transition-all text-sm"
              >
                {sending ? (
                  <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Enviando...</>
                ) : (
                  <><Send className="w-4 h-4" /> Enviar campaña</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 text-white text-sm px-5 py-3 rounded-xl shadow-xl z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
